import React from 'react';
import { Tooltip } from 'antd';

/**
 * テーブル列の共通ヘルパー
 * ------------------------------------------------------------------
 * 長文セルの「狭いと … 省略 + ホバーで全文ツールチップ」や、
 * 列ヘッダークリックでのソート関数を定型化し、各ページの重複を減らす。
 * 配色・寸法はデザイントークン（antd Tooltip テーマ）に従う。
 */

/**
 * 省略セル用ツールチップのスタイル
 * ------------------------------------------------------------------
 * antd 既定の丸い吹き出し（말풍선）ではなく、Windows ネイティブの
 * ホバーツールチップ風の「タイトな四角・米色（アイボリー）背景」にする。
 * 長文は maxWidth で適度に改行。配色・寸法はデザイントークン。
 */
const ELLIPSIS_TOOLTIP_INNER_STYLE = {
  backgroundColor: 'var(--yellow-background)', // 米色 #FEF5E0
  color: 'var(--black)',
  border: '1px solid var(--grey-30)',
  borderRadius: 'var(--radius-xs)', // 2px = タイトな四角
  padding: 'var(--space-1) var(--space-2)', // 4px 8px
  // antd 既定の min-height（テーマ controlHeight=40px 由来）を解除。
  // これが残ると 1 行テキストより箱が高くなり、下に余白が出て文字が上に寄る。
  minHeight: 'auto',
  fontSize: 'var(--text-sm)',
  lineHeight: 'var(--leading-snug)',
  boxShadow: 'var(--shadow-card)',
  maxWidth: 360, // 長文はここで折り返す
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

/**
 * 長文テキスト列を「省略 + ツールチップ」化する。
 * antd の `ellipsis` でセルを … 省略し、ホバーで全文を四角いツールチップ表示。
 *
 *   ellipsisColumn({ title: 'メール', dataIndex: 'email', width: 200 })
 *
 * 既存の `render` があればその結果をツールチップで包む。render が文字列/数値を
 * 返す場合はそれを、ノードを返す場合は元の値（value）をツールチップ本文にする。
 * StatusBadge 等のノードを返す列には使わない（テキスト列専用）。
 *
 * `subline: (record, index) => node` を渡すと、テキストの下の行にバッジ等を添える。
 * テキストは従来どおり 1 行で … 省略され（バッジに幅を取られない）、subline がある行だけ高くなる。
 * subline は全文ツールチップの外に置く（subline 自身のツールチップと二重に出さないため）。
 *
 *   ellipsisColumn({ title: '名前', dataIndex: 'fullName', width: 140,
 *     subline: (record) => (record.inactive ? <InactiveBadge /> : null) })
 */
export function ellipsisColumn(column) {
  const { render, subline, ...rest } = column;
  return {
    ...rest,
    ellipsis: { showTitle: false },
    render: (value, record, index) => {
      const display = render ? render(value, record, index) : value;
      const extra = subline ? subline(record, index) : null;
      if (display === null || display === undefined || display === '') {
        return extra || display;
      }
      const title =
        typeof display === 'string' || typeof display === 'number'
          ? display
          : value;
      if (!extra) {
        return (
          <Tooltip
            placement="topLeft"
            title={title}
            arrow={false}
            styles={{ body: ELLIPSIS_TOOLTIP_INNER_STYLE }}
          >
            {display}
          </Tooltip>
        );
      }
      return (
        <>
          <Tooltip
            placement="topLeft"
            title={title}
            arrow={false}
            styles={{ body: ELLIPSIS_TOOLTIP_INNER_STYLE }}
          >
            <div style={SUBLINE_TEXT_STYLE}>{display}</div>
          </Tooltip>
          <div>{extra}</div>
        </>
      );
    },
  };
}

// subline 付きセル: 1 行目のテキストを列幅で … 省略する（2 行目は subline）
const SUBLINE_TEXT_STYLE = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** 文字列ソート（日本語ロケール）。`textSorter('email')` を列の sorter に。 */
export const textSorter = (key) => (a, b) =>
  String(a[key] ?? '').localeCompare(String(b[key] ?? ''), 'ja');

/** 数値ソート。`numberSorter('count')`。 */
export const numberSorter = (key) => (a, b) =>
  (Number(a[key]) || 0) - (Number(b[key]) || 0);

/** 日付ソート（パース可能な日付文字列/Date）。`dateSorter('createdAt')`。 */
export const dateSorter = (key) => (a, b) =>
  new Date(a[key] || 0).getTime() - new Date(b[key] || 0).getTime();
